import type { Database } from 'node-sqlite3-wasm';
import { isCliImmediateShutdownRequested } from '../../runtime/cli-process-interrupt.js';
import { CliSqliteInterrupted } from '../sqlite-errors.js';
import { applySqliteConcurrencyPragmas, applySqliteMemoryPragmas } from '../sqlite-pragmas.js';
import type { SqliteDatabaseAccessOptions } from '../database-access-types.js';
import { SqliteDatabaseConstructor, type SqliteMigrateState } from './bindings.js';

export function safeCloseSqliteDatabase(db: Database | null | undefined): void {
  if (db == null) return;
  try {
    if (db.isOpen) db.close();
  } catch {
    /* ignore */
  }
}

function shouldSkipMigrate(sqlitePath: string, db: Database, migrateState: SqliteMigrateState): boolean {
  if (migrateState.migrateDoneForPath !== sqlitePath) return false;
  try {
    const row = db.get(
      "SELECT 1 as ok FROM sqlite_master WHERE type='table' AND name='__migrations' LIMIT 1",
    ) as { ok: number } | null;
    return row != null;
  } catch {
    return false;
  }
}

export function openSqliteConnection<OpenOptions>(
  config: SqliteDatabaseAccessOptions<OpenOptions>,
  migrateState: SqliteMigrateState,
  options?: OpenOptions,
): Database {
  if (isCliImmediateShutdownRequested()) throw new CliSqliteInterrupted();
  const sqlitePath = config.getPath();
  config.ensureParentDir(sqlitePath);
  const db = new SqliteDatabaseConstructor(sqlitePath);
  try {
    applySqliteConcurrencyPragmas(db);
    applySqliteMemoryPragmas(db);
    if (!shouldSkipMigrate(sqlitePath, db, migrateState)) {
      config.migrate(db);
      migrateState.migrateDoneForPath = sqlitePath;
    }
    config.afterOpen?.(db, options);
  } catch (e) {
    safeCloseSqliteDatabase(db);
    throw e;
  }
  return db;
}
